import { auth } from './firebase';
import { resolveRecipeImageContentType } from '../utils/recipeImageFile';
import { fileToBase64 } from '../utils/fileBase64';

const MEDIA_API_BASE = '/api/mealpai-ai';

export type MediaScope = 'user_avatar' | 'user_cover' | 'recipe_image' | 'chef_image';

export interface MediaEntityIds {
    recipeId?: string;
    chefId?: string;
}

export interface MediaUploadUrlResponse {
    uploadUrl: string;
    objectPath: string;
    readUrl: string;
    mediaId?: string;
    imageId?: string;
    expiresAt: string;
}

export interface MediaIngestResponse {
    objectPath: string;
    readUrl: string;
    mediaId?: string;
    imageId?: string;
    contentType: string;
}

async function getAuthHeaders(): Promise<HeadersInit> {
    const currentUser = auth.currentUser;
    if (!currentUser) {
        throw new Error('Not authenticated');
    }
    const token = await currentUser.getIdToken();
    return {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
    };
}

export function isStoredMediaUrl(url?: string | null): boolean {
    return !!url && url.includes('/media/read/');
}

export function shouldIngestMediaUrl(url?: string | null): boolean {
    const trimmed = url?.trim();
    if (!trimmed) return false;
    return !isStoredMediaUrl(trimmed);
}

export async function requestMediaUploadUrl(
    scope: MediaScope,
    contentType: string,
    entityIds: MediaEntityIds = {},
): Promise<MediaUploadUrlResponse> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${MEDIA_API_BASE}/media/upload-url`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ scope, contentType, ...entityIds }),
    });

    if (!response.ok) {
        const payload = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(payload.error || 'Failed to create upload URL.');
    }

    return response.json() as Promise<MediaUploadUrlResponse>;
}

export async function uploadMediaFile(
    scope: MediaScope,
    file: File,
    entityIds: MediaEntityIds = {},
): Promise<MediaUploadUrlResponse> {
    const contentType = resolveRecipeImageContentType(file);
    const dataBase64 = await fileToBase64(file);
    const headers = await getAuthHeaders();
    const response = await fetch(`${MEDIA_API_BASE}/media/upload`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ scope, contentType, dataBase64, ...entityIds }),
    });

    if (!response.ok) {
        const payload = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(payload.error || 'Failed to upload image.');
    }

    const result = await response.json() as MediaIngestResponse;
    return {
        uploadUrl: '',
        objectPath: result.objectPath,
        readUrl: result.readUrl,
        mediaId: result.mediaId,
        imageId: result.imageId,
        expiresAt: new Date().toISOString(),
    };
}

export async function ingestMediaFromUrl(
    scope: MediaScope,
    sourceUrl: string,
    entityIds: MediaEntityIds = {},
): Promise<MediaIngestResponse> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${MEDIA_API_BASE}/media/ingest`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ scope, sourceUrl, ...entityIds }),
    });

    if (!response.ok) {
        const payload = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(payload.error || 'Failed to store image.');
    }

    return response.json() as Promise<MediaIngestResponse>;
}

export async function resolveMediaUrlForSave(
    scope: MediaScope,
    url: string | undefined,
    previousUrl?: string,
    entityIds: MediaEntityIds = {},
): Promise<{ url?: string; mediaId?: string; imageId?: string }> {
    const trimmed = url?.trim();
    if (!trimmed) {
        return { url: undefined };
    }

    const previousTrimmed = previousUrl?.trim();
    if (previousTrimmed && trimmed === previousTrimmed) {
        return { url: trimmed };
    }

    if (isStoredMediaUrl(trimmed)) {
        return { url: trimmed };
    }

    const ingested = await ingestMediaFromUrl(scope, trimmed, entityIds);
    return {
        url: ingested.readUrl,
        mediaId: ingested.mediaId,
        imageId: ingested.imageId,
    };
}
